import { AppPlans } from '@app/core/enums/app-plans.enum';
import { UserRoles } from '@app/core/enums/user-roles.enum';
import { normalizePlanName } from '@app/core/utils/plan-name.util';
import { IUserData } from '@app/modules/auth/interfaces/user-data.interface';

/** A coach's free trial: how many free sessions they had and how many are left. */
export interface IFreeSessions {
    total: number;
    left: number;
}

/**
 * Free trial sessions of a regular coach on the Free plan (granted at registration).
 * Mirrors the backend rule: paid, complimentary, premium and admin accounts have no trial.
 *
 * @returns The trial, or null when the user has none
 */
export function getFreeSessions(user: IUserData | null | undefined): IFreeSessions | null {
    const trial = user?.freeTrial;
    if (!user || !trial?.total || user.role !== UserRoles.User) {
        return null;
    }

    if (normalizePlanName(user.plan?.planName) !== AppPlans.Free || user.plan?.complimentary) {
        return null;
    }

    return { total: trial.total, left: Math.max(trial.total - (trial.used ?? 0), 0) };
}

/**
 * Whether the coach should be offered a subscription on the paywall: a regular coach on the Free plan.
 * Paying, complimentary, premium and admin accounts never see it.
 */
export function isPaywallUser(user: IUserData | null | undefined): boolean {
    return (
        !!user &&
        user.role === UserRoles.User &&
        normalizePlanName(user.plan?.planName) === AppPlans.Free &&
        !user.plan?.complimentary
    );
}
